import { useState } from 'react'
import { icons } from 'cdh-data-portal-theme'
import { businessObjectFriendlyName, hubFriendlyName } from '../DataGovernorsPage.static'
import type { SelectedFilterValues } from './RegistryFiltersDialog/RegistryFiltersDialog.logic'
import type { FilterFacet, FilterFacetKey } from './RegistryFilters.static'
import { localText } from './RegistryFilters.static'

export interface UseRegistryFiltersArgs {
  readonly hubFilter: string
  readonly onHubFilterChange: (value: string) => void
  readonly businessObjectFilter: string
  readonly onBusinessObjectFilterChange: (value: string) => void
  readonly hubOptions: readonly string[]
  readonly businessObjectOptions: readonly string[]
  readonly hubFriendlyNames: Readonly<Record<string, string>>
}

export function useRegistryFilters({
  hubFilter,
  onHubFilterChange,
  businessObjectFilter,
  onBusinessObjectFilterChange,
  hubOptions,
  businessObjectOptions,
  hubFriendlyNames,
}: UseRegistryFiltersArgs) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const facets: readonly FilterFacet[] = [
    {
      key: 'hub',
      title: localText.hubFilterLabel,
      icon: icons.folderCompact,
      options: hubOptions.map((hub) => ({ id: hub, name: hubFriendlyName(hub, hubFriendlyNames) })),
    },
    {
      key: 'businessObject',
      title: localText.businessObjectFilterLabel,
      icon: icons.objectCompact,
      options: businessObjectOptions.map((businessObject) => ({
        id: businessObject,
        name: businessObjectFriendlyName(businessObject),
      })),
    },
  ]

  const selectedValues: SelectedFilterValues = { hub: hubFilter, businessObject: businessObjectFilter }

  function applyValues(values: SelectedFilterValues) {
    if (values.hub !== hubFilter) {
      onHubFilterChange(values.hub)
    }
    if (values.businessObject !== businessObjectFilter) {
      onBusinessObjectFilterChange(values.businessObject)
    }
  }

  function clearFacet(key: FilterFacetKey) {
    if (key === 'hub') {
      onHubFilterChange('')
    } else {
      onBusinessObjectFilterChange('')
    }
  }

  function clearAll() {
    onHubFilterChange('')
    onBusinessObjectFilterChange('')
  }

  return {
    facets,
    selectedValues,
    isDialogOpen,
    openDialog: () => setIsDialogOpen(true),
    closeDialog: () => setIsDialogOpen(false),
    applyValues,
    clearFacet,
    clearAll,
  }
}
